// data/categories.ts
import { Category, CategoryGroup, CategoryItem } from '../types/category';

// Category group definitions
export const CATEGORY_GROUPS: CategoryGroup[] = [
  { id: 'fiction', name: 'Fiction', icon: '📖', color: '#3B82F6', description: 'Imaginative stories across literary genres' },
  { id: 'thematic-fiction', name: 'Thematic Fiction', icon: '🎭', color: '#8B5CF6', description: 'Narrative stories exploring ideas and disciplines' },
  { id: 'topics', name: 'Topics & Lifestyles', icon: '💼', color: '#F97316', description: 'Stories set in practical, everyday subjects' },
  { id: 'real-world', name: 'Real World', icon: '🌍', color: '#0EA5E9', description: 'Real people and actual events' },
];

// Category definitions - metadata only, content loaded from separate files
export const CATEGORIES: Category[] = [
  // Fiction
  { id: 'scifi', name: 'Science Fiction', description: 'Futuristic stories and speculative technology', icon: 'Rocket', itemCount: 52, color: '#3B82F6', groupId: 'fiction', group: 'Fiction' },
  { id: 'fantasy', name: 'Fantasy', description: 'Magical worlds and mythical creatures', icon: 'Wand2', itemCount: 37, color: '#8B5CF6', groupId: 'fiction', group: 'Fiction' },
  { id: 'mystery', name: 'Mystery', description: 'Puzzles, clues, and enigmatic narratives', icon: 'Search', itemCount: 17, color: '#6366F1', groupId: 'fiction', group: 'Fiction' },
  { id: 'romance', name: 'Romance', description: 'Love stories and romantic relationships', icon: 'Heart', itemCount: 30, color: '#EC4899', groupId: 'fiction', group: 'Fiction' },
  { id: 'horror', name: 'Horror', description: 'Dark and frightening tales', icon: 'Skull', itemCount: 17, color: '#991B1B', groupId: 'fiction', group: 'Fiction' },
  { id: 'adventure', name: 'Adventure', description: 'Exciting journeys and quests', icon: 'Compass', itemCount: 17, color: '#F59E0B', groupId: 'fiction', group: 'Fiction' },
  { id: 'historical', name: 'Historical Fiction', description: 'Stories set in historical periods', icon: 'BookOpen', itemCount: 37, color: '#78350F', groupId: 'fiction', group: 'Fiction' },
  { id: 'comedy', name: 'Comedy', description: 'Humorous and lighthearted stories', icon: 'Smile', itemCount: 17, color: '#10B981', groupId: 'fiction', group: 'Fiction' },
  { id: 'drama', name: 'Drama', description: 'Emotional and character-driven stories', icon: 'Drama', itemCount: 25, color: '#F43F5E', groupId: 'fiction', group: 'Fiction' },
  { id: 'crime', name: 'Crime', description: 'Crime stories featuring investigations, heists, mysteries, and the pursuit of justice', icon: 'Shield', itemCount: 31, color: '#7C3AED', groupId: 'fiction', group: 'Fiction' },
  { id: 'thriller', name: 'Thriller', description: 'Suspenseful tales of tension, danger, and unexpected twists', icon: 'AlertTriangle', itemCount: 25, color: '#DC2626', groupId: 'fiction', group: 'Fiction' },
  { id: 'western', name: 'Western', description: 'Tales of the Old West — gunfighters, marshals, outlaws, and frontier justice', icon: 'Sun', itemCount: 25, color: '#B45309', groupId: 'fiction', group: 'Fiction' },

  // Thematic Fiction
  { id: 'science', name: 'Science', description: 'Scientific writings and discoveries', icon: 'Flask', itemCount: 17, color: '#14B8A6', groupId: 'thematic-fiction', group: 'Thematic Fiction' },
  { id: 'philosophy', name: 'Philosophy', description: 'Philosophical thoughts and arguments', icon: 'Book', itemCount: 17, color: '#64748B', groupId: 'thematic-fiction', group: 'Thematic Fiction' },
  { id: 'psychology', name: 'Psychology', description: 'Mind, behavior, and mental processes', icon: 'Brain', itemCount: 17, color: '#D946EF', groupId: 'thematic-fiction', group: 'Thematic Fiction' },
  { id: 'creative-history', name: 'Creative History', description: 'Historical accounts and analysis', icon: 'Clock', itemCount: 17, color: '#A855F7', groupId: 'thematic-fiction', group: 'Thematic Fiction' },
  { id: 'art', name: 'Art & Culture', description: 'Art, music, and cultural analysis', icon: 'Palette', itemCount: 17, color: '#F43F5E', groupId: 'thematic-fiction', group: 'Thematic Fiction' },
  { id: 'nature', name: 'Nature & Environment', description: 'Nature writing and environmental topics', icon: 'Leaf', itemCount: 17, color: '#22C55E', groupId: 'thematic-fiction', group: 'Thematic Fiction' },

  // Topics & Lifestyles
  { id: 'business', name: 'Business & Economics', description: 'Business strategies and economic theory', icon: 'Briefcase', itemCount: 38, color: '#F97316', groupId: 'topics', group: 'Topics & Lifestyles' },
  { id: 'technology', name: 'Technology', description: 'Tech innovations and digital culture', icon: 'Cpu', itemCount: 17, color: '#06B6D4', groupId: 'topics', group: 'Topics & Lifestyles' },
  { id: 'health', name: 'Health & Wellness', description: 'Health, fitness, and wellbeing', icon: 'HeartPulse', itemCount: 17, color: '#EF4444', groupId: 'topics', group: 'Topics & Lifestyles' },
  { id: 'sports', name: 'Sports', description: 'Athletic competition, perseverance, and the drama of the game', icon: 'Trophy', itemCount: 26, color: '#22C55E', groupId: 'topics', group: 'Topics & Lifestyles' },
  { id: 'travel', name: 'Travel', description: 'Travel stories and destinations', icon: 'MapPin', itemCount: 17, color: '#84CC16', groupId: 'topics', group: 'Topics & Lifestyles' },

  // Real World (non-fiction)
  { id: 'biography', name: 'Biography & Memoir', description: 'Life stories and personal recollections', icon: 'User', itemCount: 17, color: '#0EA5E9', groupId: 'real-world', group: 'Real World' },
  { id: 'history', name: 'History', description: 'Historical accounts and analysis of pivotal moments that shaped our world', icon: 'Landmark', itemCount: 25, color: '#B45309', groupId: 'real-world', group: 'Real World' },
];

// Map of category IDs to their content loaders
// All categories use fetch-based loading from metadata.json files
export const categoryContentLoaders: Record<string, () => Promise<{ default: CategoryItem[] }>> = {
  scifi: async () => {
    const response = await fetch('/data/categories/scifi/metadata.json');
    if (!response.ok) throw new Error(`Failed to load scifi: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('scifi', metadata) };
  },
  fantasy: async () => {
    const response = await fetch('/data/categories/fantasy/metadata.json');
    if (!response.ok) throw new Error(`Failed to load fantasy: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('fantasy', metadata) };
  },
  mystery: async () => {
    const response = await fetch('/data/categories/mystery/metadata.json');
    if (!response.ok) throw new Error(`Failed to load mystery: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('mystery', metadata) };
  },
  romance: async () => {
    const response = await fetch('/data/categories/romance/metadata.json');
    if (!response.ok) throw new Error(`Failed to load romance: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('romance', metadata) };
  },
  horror: async () => {
    const response = await fetch('/data/categories/horror/metadata.json');
    if (!response.ok) throw new Error(`Failed to load horror: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('horror', metadata) };
  },
  adventure: async () => {
    const response = await fetch('/data/categories/adventure/metadata.json');
    if (!response.ok) throw new Error(`Failed to load adventure: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('adventure', metadata) };
  },
  historical: async () => {
    const response = await fetch('/data/categories/historical/metadata.json');
    if (!response.ok) throw new Error(`Failed to load historical: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('historical', metadata) };
  },
  comedy: async () => {
    const response = await fetch('/data/categories/comedy/metadata.json');
    if (!response.ok) throw new Error(`Failed to load comedy: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('comedy', metadata) };
  },
  drama: async () => {
    const response = await fetch('/data/categories/drama/metadata.json');
    if (!response.ok) throw new Error(`Failed to load drama: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('drama', metadata) };
  },
  biography: async () => {
    const response = await fetch('/data/categories/biography/metadata.json');
    if (!response.ok) throw new Error(`Failed to load biography: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('biography', metadata) };
  },
  science: async () => {
    const response = await fetch('/data/categories/science/metadata.json');
    if (!response.ok) throw new Error(`Failed to load science: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('science', metadata) };
  },
  'creative-history': async () => {
    const response = await fetch('/data/categories/creative-history/metadata.json');
    if (!response.ok) throw new Error(`Failed to load creative-history: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('creative-history', metadata) };
  },
  technology: async () => {
    const response = await fetch('/data/categories/technology/metadata.json');
    if (!response.ok) throw new Error(`Failed to load technology: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('technology', metadata) };
  },
  nature: async () => {
    const response = await fetch('/data/categories/nature/metadata.json');
    if (!response.ok) throw new Error(`Failed to load nature: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('nature', metadata) };
  },
  health: async () => {
    const response = await fetch('/data/categories/health/metadata.json');
    if (!response.ok) throw new Error(`Failed to load health: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('health', metadata) };
  },
  business: async () => {
    const response = await fetch('/data/categories/business/metadata.json');
    if (!response.ok) throw new Error(`Failed to load business: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('business', metadata) };
  },
  psychology: async () => {
    const response = await fetch('/data/categories/psychology/metadata.json');
    if (!response.ok) throw new Error(`Failed to load psychology: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('psychology', metadata) };
  },
  travel: async () => {
    const response = await fetch('/data/categories/travel/metadata.json');
    if (!response.ok) throw new Error(`Failed to load travel: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('travel', metadata) };
  },
  art: async () => {
    const response = await fetch('/data/categories/art/metadata.json');
    if (!response.ok) throw new Error(`Failed to load art: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('art', metadata) };
  },
  philosophy: async () => {
    const response = await fetch('/data/categories/philosophy/metadata.json');
    if (!response.ok) throw new Error(`Failed to load philosophy: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('philosophy', metadata) };
  },
  crime: async () => {
    const response = await fetch('/data/categories/crime/metadata.json');
    if (!response.ok) throw new Error(`Failed to load crime: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('crime', metadata) };
  },
  sports: async () => {
    const response = await fetch('/data/categories/sports/metadata.json');
    if (!response.ok) throw new Error(`Failed to load sports: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('sports', metadata) };
  },
  history: async () => {
    const response = await fetch('/data/categories/history/metadata.json');
    if (!response.ok) throw new Error(`Failed to load history: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('history', metadata) };
  },
  thriller: async () => {
    const response = await fetch('/data/categories/thriller/metadata.json');
    if (!response.ok) throw new Error(`Failed to load thriller: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('thriller', metadata) };
  },
  western: async () => {
    const response = await fetch('/data/categories/western/metadata.json');
    if (!response.ok) throw new Error(`Failed to load western: ${response.status}`);
    const metadata = await response.json();
    return { default: transformMetadata('western', metadata) };
  },
};

// Helper to get all categories
export const getCategories = (): Category[] => {
  return CATEGORIES;
};

// Helper to get a single category by ID
export const getCategory = (categoryId: string): Category | undefined => {
  return CATEGORIES.find(cat => cat.id === categoryId);
};

// Transform metadata.json format to CategoryItem[] format
interface StorySection {
  id: string;
  title: string;
  wordCount: number;
  startWordIndex: number;
  endWordIndex: number;
  contentPath: string;
}

interface StoryMetadata {
  id: string;
  title: string;
  wordCount: number;
  spoilerFreeSummary: string;
  difficulty: string;
  sections: StorySection[];
}

interface CategoryMetadata {
  stories: StoryMetadata[];
}

function transformMetadata(categoryId: string, metadata: CategoryMetadata): CategoryItem[] {
  return metadata.stories.map((story: StoryMetadata) => ({
    id: `${categoryId}-${story.id}`,
    title: story.title,
    content: '',
    spoilerFreeSummary: story.spoilerFreeSummary,
    categoryId: categoryId,
    wordCount: story.wordCount,
    readingTimeMinutes: Math.ceil(story.wordCount / 200),
    difficulty: story.difficulty as 'beginner' | 'intermediate' | 'advanced',
    source: 'Flash-Read RSVP Content',
    sections: story.sections?.map((section) => ({
      id: section.id,
      title: section.title,
      contentPath: section.contentPath,
      wordCount: section.wordCount,
      startWordIndex: section.startWordIndex,
      endWordIndex: section.endWordIndex,
    })),
    useSections: true,
  }));
}
